import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Container, Divider, List, ListItemButton, ListItemIcon, ListItemText, MenuItem,
  Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  WhatsApp as NumberIcon,
  CallSplit as AnswersIcon,
  EventAvailable as BookingIcon,
  Apartment as WorkspaceIcon,
  CreditCard as BillingIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import OrganizationsSettings from './OrganizationsSettings';
import EnvironmentsSettings from './EnvironmentsSettings';
import PermissionsSettings from './PermissionsSettings';
import PreferencesSettings from './PreferencesSettings';
import SecuritySettings from './SecuritySettings';
import DemoSettingsNotice from './DemoSettingsNotice';
import AdminSettings from './AdminSettings';
import BillingSettings from './BillingSettings';
import BookingSettings from './BookingSettings';
import WelcomeChipsSettings from './WelcomeChipsSettings';
import ConnectionsSettings from './ConnectionsSettings';
import DeliveriesSettings from './DeliveriesSettings';
import ApiKeysSettings from './ApiKeysSettings';
import YourNumberSettings from './YourNumberSettings';
import HowYouWorkSettings from './HowYouWorkSettings';
import WorkspaceNameSettings from './WorkspaceNameSettings';
import { useWorkspaceShape } from '../../hooks/useWorkspaceShape';
import { AD_ATTRIBUTION_ENABLED } from '../../features';

/**
 * Settings as a handful of subjects, not a row of tabs.
 *
 * There were eleven tabs in a horizontal scroller, mixing four unrelated kinds of thing: the
 * booking diary (which was daily work and has moved to /bookings), the channel, where answers
 * go, and account administration. Nine of them fitted on a laptop and five on a phone, so the
 * rest were reachable only by scrolling a strip most people do not know scrolls.
 *
 * Each section here is one question somebody actually arrives with, "what number do I send
 * from", "where do the answers go", and the panels inside it are unchanged. A section can hold
 * several: "Answers go to" is the destinations, the accounts they authenticate with, and the
 * keys for reaching them programmatically, which were three tabs describing one subject.
 */
const SECTIONS = {
  number: {
    label: 'Your number',
    hint: 'The WhatsApp number your flows go out from, and what greets people on it',
    icon: <NumberIcon />,
    panels: [YourNumberSettings, WelcomeChipsSettings],
  },
  answers: {
    label: 'Answers go to',
    hint: 'Where a completed flow is delivered, and what it uses to get there',
    icon: <AnswersIcon />,
    panels: [DeliveriesSettings, ConnectionsSettings, ApiKeysSettings],
  },
  booking: {
    label: 'Booking setup',
    hint: 'Services, opening hours and calendar sync. The diary itself is under Bookings',
    icon: <BookingIcon />,
    panels: [BookingSettings],
  },
  workspace: {
    label: 'Workspace',
    hint: 'How you work, and what that means this page has to show you',
    icon: <WorkspaceIcon />,
    panels: [HowYouWorkSettings],
  },
  billing: {
    label: 'Billing',
    hint: 'Your plan and what it includes',
    icon: <BillingIcon />,
    panels: [BillingSettings],
  },
  demo: {
    label: 'Settings',
    hint: 'What an account unlocks',
    icon: <WorkspaceIcon />,
    panels: [DemoSettingsNotice],
  },
  admin: {
    label: 'Admin',
    hint: 'FlowGate tooling',
    icon: <AdminIcon />,
    panels: [AdminSettings],
  },
};

/**
 * Where the old `?tab=` links land. Settings URLs are pasted into notes and support replies, and
 * a link that used to open Deliveries should not open the first section it happens to find.
 */
const TAB_TO_SECTION = {
  organizations: 'workspace',
  environments: 'workspace',
  permissions: 'workspace',
  preferences: 'workspace',
  booking: 'booking',
  welcomeChips: 'number',
  connections: 'answers',
  deliveries: 'answers',
  apiKeys: 'answers',
  billing: 'billing',
  admin: 'admin',
};

const SettingsPage = () => {
  const { selectedOrganization, isOwner } = useOrganization();
  const { can } = usePermissions();
  const { showTeamTools, showWorkspaceTools } = useWorkspaceShape();
  const { isDemoMode } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));

  const isOwnerOfOrg = selectedOrganization ? isOwner(selectedOrganization.id) : false;
  const canViewPermissions = can.readPermissions || can.readRoles || can.readMembers;
  const worksWithFlows = can.modifyFlow || can.readFlow;

  const available = useMemo(() => {
    // A demo account cannot change any of this, the server refuses it (DemoRestrictionFilter),
    // so offering the screens would be offering doors that do not open. One page saying what an
    // account is for, instead.
    if (isDemoMode) return ['demo'];
    const sections = ['number'];
    if (worksWithFlows) sections.push('answers', 'booking');
    sections.push('workspace', 'billing');
    if (can.isAppAdmin) sections.push('admin');
    return sections;
  }, [isDemoMode, worksWithFlows, can.isAppAdmin]);

  const requested = searchParams.get('section') || TAB_TO_SECTION[searchParams.get('tab')];
  const active = available.includes(requested) ? requested : available[0];

  // Old links keep working, and the URL says where it actually landed.
  useEffect(() => {
    if (searchParams.get('section') === active) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.set('section', active);
    setSearchParams(next, { replace: true });
  }, [active, searchParams, setSearchParams]);

  const section = SECTIONS[active];

  const panelsFor = (name) => {
    if (name !== 'workspace') return section.panels;
    // Two gates, and the shape gate comes first. Permissions decide whether somebody is ALLOWED
    // to see members or environments; the two answers at the top of this section decide whether
    // this account has any use for them. An owner of a one-person, one-number workspace is
    // allowed to administer members and environments, and has nothing to administer.
    return [
      HowYouWorkSettings,
      ...(showWorkspaceTools ? [OrganizationsSettings] : [WorkspaceNameSettings]),
      ...(showWorkspaceTools && isOwnerOfOrg ? [EnvironmentsSettings] : []),
      ...(showTeamTools && canViewPermissions ? [PermissionsSettings] : []),
      ...(AD_ATTRIBUTION_ENABLED ? [PreferencesSettings] : []),
      // Last, and for everybody: it is not about how the workspace is shaped, it is about the
      // account, and it is the one thing here somebody may arrive looking for in a hurry.
      SecuritySettings,
    ];
  };

  const nav = narrow ? (
    <TextField
      select
      fullWidth
      size="small"
      label="Settings"
      value={active}
      onChange={(event) => setSearchParams({ section: event.target.value }, { replace: true })}
      sx={{ mb: 3 }}
    >
      {available.map((name) => (
        <MenuItem key={name} value={name}>{SECTIONS[name].label}</MenuItem>
      ))}
    </TextField>
  ) : (
    <List sx={{ width: 232, flexShrink: 0 }} data-testid="settings-sections">
      {available.map((name) => (
        <ListItemButton
          key={name}
          selected={name === active}
          onClick={() => setSearchParams({ section: name }, { replace: true })}
          sx={{ borderRadius: 2, mb: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 38 }}>{SECTIONS[name].icon}</ListItemIcon>
          <ListItemText primary={SECTIONS[name].label} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Settings</Typography>
        {selectedOrganization && (
          <Typography variant="body2" color="text.secondary">{selectedOrganization.name}</Typography>
        )}
      </Box>

      <Stack direction={narrow ? 'column' : 'row'} spacing={narrow ? 0 : 4} alignItems="flex-start">
        {nav}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">{section.label}</Typography>
            <Typography variant="body2" color="text.secondary">{section.hint}</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {active === 'workspace' && !selectedOrganization && (
            <Alert severity="warning" sx={{ mb: 3 }}>Please select an organization first</Alert>
          )}

          <Stack spacing={4}>
            {panelsFor(active).map((Panel, index) => (
              <Panel key={index} />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};

export default SettingsPage;
